interface BuildTreeViewParams<T> {
  items: T[];
  getPrimaryKeyOfItem: (item: T) => number;
  // return `undefined` iff it is the root
  getParentKeyOfItem: (item: T) => number | undefined | null;
  expandedItemIds: Set<number>;
}

export interface TreeViewInfo<T> {
  // whether this row has children
  hasChildren: boolean;
  // the depth of this row
  depth: number;
  // whether this row is expanded, `undefined` iff `hasChildren` is false
  // Note: This does mean we do not need `hasChildren`, but we choose to have that for better semantic
  isExpanded?: boolean;
  // The parent node of current node. `undefined` iff current node is root.
  parent: WithTreeViewData<T> | undefined;
  children: WithTreeViewData<T>[];
}

export type WithTreeViewData<T> = T & {
  treeViewInfo: TreeViewInfo<T>;
}

const dummyRootId = 0;

/**
 * Limitation: The tree view should not have pagination
 *
 * Assuming, Why and How it works: We rebuild a tree from an array of nodes.
 * To do this, we need to know
 * 1. The ID of the node.
 * 2. The ID of the parent node of the node.
 * Why ID: To build the tree from the response, reference equal is impossiable to use. ID is necessary.
 * Why parent node ID: to rebuild a tree, we need either the children of a node, or the parent of the node.
 *
 * The function is not pure.
 *
 * Not use `IGroup` of the DetailsList, we do want to show data for group row.
 */
export function buildTreeViewItems<T>(params: BuildTreeViewParams<T>): WithTreeViewData<T>[] {
  const {
    items, getParentKeyOfItem, getPrimaryKeyOfItem, expandedItemIds,
  } = params;
  const nodeById = new Map<number, T>();

  if (items.length === 0) { return []; }

  items.forEach((item) => {
    nodeById.set(getPrimaryKeyOfItem(item), item);
  });

  const childrenIdByNodeId = getNodesWithChildren();

  // now root and children of keys are known, return data by order

  // The real root should be the only child of dummy root.
  const rootId = childrenIdByNodeId.get(dummyRootId)![0];

  const res: WithTreeViewData<T>[] = [];

  // always traverse the whole tree to attach extra data to all nodes
  function buildTree(nodeId: number, parentNode: T | undefined, currentLevel: number) {
    const rootNode = nodeById.get(nodeId)!;
    (rootNode as any as WithTreeViewData<T>).treeViewInfo = {
      depth: currentLevel,
      hasChildren: childrenIdByNodeId.has(nodeId),
      isExpanded: childrenIdByNodeId.has(nodeId) ? expandedItemIds.has(nodeId) : undefined,
      parent: parentNode as WithTreeViewData<T>,
      children: [],
    };

    // root node is always visiable
    // if node's parent is expanded, the node is visible.
    const isCurrentNodeVisiable = !getParentKeyOfItem(rootNode) || expandedItemIds.has(getParentKeyOfItem(rootNode)!);

    if (isCurrentNodeVisiable) {
      res.push(rootNode as any as WithTreeViewData<T>);
    }

    const childrenIds = childrenIdByNodeId.get(nodeId);

    childrenIds?.forEach((childId) => {
      (rootNode as any as WithTreeViewData<T>).treeViewInfo.children.push(nodeById.get(childId) as any);
      buildTree(childId, rootNode, currentLevel + 1);
    });
  }

  buildTree(rootId, undefined, 1);

  return res;

  // Return a map whose key is the ID of the node, the value is an array of IDs of its children nodes.
  // leaf does not have correspond key.
  function getNodesWithChildren() {
    const map = new Map<number, number[]>();
    items.forEach((item) => {
      const primaryKey = getPrimaryKeyOfItem(item);
      // 0 is the dummy root
      const parentKey = getParentKeyOfItem(item) ?? dummyRootId;

      // initial
      if (!map.has(parentKey)) {
        map.set(parentKey, []);
      }

      // insert children
      const childrenKeys = map.get(parentKey);

      childrenKeys!.push(primaryKey);
    })
    return map;
  }
}

export function isLeaf<T>(item: WithTreeViewData<T>): boolean { return item.treeViewInfo.children.length === 0; }

export function isRoot<T>(item: WithTreeViewData<T>): boolean { return item.treeViewInfo.parent === undefined; }

export function getRootOfNode<T>(item: WithTreeViewData<T>): WithTreeViewData<T> {
  let node = item;
  while (!isRoot(node)) {
    node = node.treeViewInfo.parent!;
  }
  return node;
}

export function getSiblingsOfNode<T>(item: WithTreeViewData<T>): WithTreeViewData<T>[] | undefined {
  if (isRoot(item)) { return undefined; }
  return item.treeViewInfo.parent!.treeViewInfo.children.filter(child => child !== item);
}

export function getAllChildrenOfNode<T>(item: WithTreeViewData<T>): WithTreeViewData<T>[] {
  const res: WithTreeViewData<T>[] = [];
  traverseTreeByPreOrder(item, currentItem => res.push(currentItem));
  return res.filter(cur => cur !== item);
}

export function traverseTreeByPreOrder<T>(root: WithTreeViewData<T>, action: (item: WithTreeViewData<T>) => void) {
  action(root);
  root.treeViewInfo.children.forEach((child) => {
    traverseTreeByPreOrder(child, action);
  });
}
